import { Session } from '@ory/client-fetch';
import { Flex, Portal } from '@radix-ui/themes';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import _ReactGA from 'react-ga4';
// Vite/Rollup CJS interop: in prod builds the default import may be the module wrapper
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ReactGA = ((_ReactGA as any).default ?? _ReactGA) as typeof _ReactGA;
import { Toaster } from 'sonner';
import { DelayedRenderingComponent } from './components/DelayedRenderingComponent';
import { EmailVerificationOverlay } from './components/EmailVerificationOverlay/EmailVerificationOverlay';
import { RouteErrorBoundary } from './components/errors/RouteErrorBoundary';
import { RouteNotFoundErrorBoundary } from './components/errors/RouteNotFoundErrorBoundary';
import { LoadingSpinner } from './components/LoadingSpinner/LoadingSpinner';
import AppProviders from './context/AppProviders';
import { useSession } from './features/auth/context/Session';
import { Releases } from './features/releases/Releases';
import { useOrganisation } from './hooks/useOrganisation';
import { mustVerifyEmail } from './lib/ory/emailVerification';
import { RadixThemeProvider } from './lib/radix/RadixThemeProvider';
import { defaultQueryClient } from './lib/tanstackQuery';
import { routeTree } from './routeTree.gen';
import { routesWithoutOrganisation } from './routes/routeExemptions';
import { services } from './services';
import './styles/app.css';
import './styles/global.scss';
import 'katex/dist/katex.min.css';
import { GOOGLE_ANALYTICS_ID } from './utils/constants';
import { interfaceStore } from './stores/interface';
import { useStore } from '@nanostores/react';

ReactGA.initialize(GOOGLE_ANALYTICS_ID);

// Set up a Router instance
const router = createRouter({
  routeTree,
  context: {
    session: null!,
    isSsoSetupRequired: false,
    queryClient: defaultQueryClient,
    organisation: undefined,
    organisations: undefined,
    services: services,
  },
  // Fuzzy matching to preserve as much parent layout as possible for the user gives them more context to navigate to a useful location based on where they thought they would arrive.
  notFoundMode: 'fuzzy',
  defaultPreload: 'intent',
  // Since we're using React Query, we don't want loader calls to ever be stale
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteErrorBoundary,
  defaultNotFoundComponent: RouteNotFoundErrorBoundary,
  // defaultPendingComponent: () => <Loading />,
});

// Register things for typesafety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

function Router({ onSetCurrentSession }: { onSetCurrentSession: (session: Session) => void }) {
  const { organisation, organisations } = useOrganisation();
  const { session, isSsoSetupRequired } = useSession();
  const [isReady, setIsReady] = useState(false);
  const [allowPageToRender, setAllowPageToRender] = useState(false);

  useEffect(() => {
    if (session) {
      // Check if current route requires organisation
      const currentPath = window.location.pathname;
      const requiresOrganisation = !routesWithoutOrganisation.some((route) => currentPath.startsWith(route));

      if (requiresOrganisation) {
        if (organisation && organisations) {
          setIsReady(true);
        }
      } else {
        // For routes that don't require organisation, we can proceed
        setIsReady(true);
      }
    }
    if (session) {
      onSetCurrentSession(session);
    }
  }, [organisation, organisations, session, onSetCurrentSession]);

  if (session && mustVerifyEmail(session)) {
    return <EmailVerificationOverlay />;
  }

  if (!isReady && !allowPageToRender) {
    return (
      <Flex justify="center" align="center" height="100%" width="100%">
        <DelayedRenderingComponent delay={500} onAllowContinue={() => setAllowPageToRender(true)}>
          <LoadingSpinner width="6rem" height="6rem" />
        </DelayedRenderingComponent>
      </Flex>
    );
  }

  return <RouterProvider router={router} context={{ session, isSsoSetupRequired, organisation, organisations }} />;
}

function App() {
  const [currentSession, setCurrentSession] = useState<Session | null>(null);

  const appearanceMode = useStore(interfaceStore.appearanceMode);
  return (
    <AppProviders>
      <Suspense>
        <Router onSetCurrentSession={setCurrentSession} />
        <Portal>
          <RadixThemeProvider appearance={appearanceMode}>
            <Toaster
              className="pointer-events-auto"
              position="top-right"
              style={{ zIndex: 1000 }}
              offset={{ top: '70px', right: '20px' }}
              closeButton
              richColors
            />
          </RadixThemeProvider>
        </Portal>
        {currentSession && !mustVerifyEmail(currentSession) && (
          <Releases currentSession={currentSession} />
        )}
      </Suspense>
    </AppProviders>
  );
}

const rootElement = document.getElementById('app')!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(<App />);
}
